<!-- src/components/auth/OtpInput.vue -->
<template>
  <PinInputRoot
    ref="rootRef"
    :model-value="pinValue"
    :type="charset === 'alphanumeric' ? 'text' : 'number'"
    otp
    :disabled="disabled"
    class="flex justify-center"
    :class="[gapClass, shaking ? 'otp-shake' : '']"
    @update:model-value="onUpdate"
    @paste.capture="onPasteCapture"
    @beforeinput.capture="onBeforeInputCapture"
    @input.capture="onInputCapture"
  >
    <PinInputInput
      v-for="(_, i) in length"
      :key="i"
      :index="i"
      data-otp-box=""
      :aria-label="$t(charset === 'alphanumeric' ? 'auth.otp.charLabel' : 'auth.otp.digitLabel', { index: i + 1, total: length })"
      :autocapitalize="charset === 'alphanumeric' ? 'characters' : undefined"
      :spellcheck="charset === 'alphanumeric' ? false : undefined"
      :aria-invalid="invalid ? 'true' : undefined"
      :class="[
        boxClass,
        'text-center font-bold rounded-xl bg-white caret-brand',
        'border-2 transition-colors outline-none disabled:opacity-60',
        'focus:border-brand focus:ring-2 focus:ring-brand/30',
        boxStateClass(i),
      ]"
    />
  </PinInputRoot>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue'
import { PinInputInput, PinInputRoot } from 'reka-ui'

const props = withDefaults(defineProps<{
  modelValue: string
  length?: number
  /** Pendant la vérification : les cases se figent au lieu d'accepter une 2e saisie. */
  disabled?: boolean
  /** Code refusé : bordure et chiffres en rouge, plus `aria-invalid` sur chaque case. */
  invalid?: boolean
  /**
   * `digits` pour un OTP GoTrue, `alphanumeric` pour un code d'invitation
   * (8 caractères A-Z0-9). Le second bascule reka-ui en `type="text"` : son
   * mode `number` refuserait les lettres, et le pavé numérique du téléphone
   * n'aurait rien à proposer.
   */
  charset?: 'digits' | 'alphanumeric'
}>(), { length: 6, charset: 'digits' })

const emit = defineEmits<{ (e: 'update:modelValue', v: string): void; (e: 'complete', v: string): void }>()

// Au-delà de 6 cases, la taille d'origine déborde : 8 x 48px + gouttières
// dépassent les 343px utiles d'un écran de 375. On resserre plutôt que de
// laisser la rangée passer à la ligne, une grille de code coupée en deux ne se
// lit plus comme un code.
const boxClass = computed(() => (props.length > 6 ? 'w-9 h-12 text-lg' : 'w-12 h-14 text-2xl'))
const gapClass = computed(() => (props.length > 6 ? 'gap-1.5' : 'gap-2.5'))

const digits = computed(() => props.modelValue.split(''))

// Trois états de case, dans cet ordre de priorité : refusé, rempli, vide. Le
// rouge l'emporte sur le soleil, sinon un code faux resterait peint comme un
// code valide.
function boxStateClass(i: number): string {
  if (props.invalid) return 'border-danger text-danger'
  return digits.value[i] ? 'border-brand text-ink' : 'border-line-strong text-ink'
}

// Le composant expose une CHAÎNE au parent (contrat inchangé) là où reka-ui
// travaille sur un tableau. `type="number"` veut des nombres, d'où le mapping
// dans les deux sens plutôt qu'un v-model direct.
// Le cast est assumé : reka-ui résout le type de `modelValue` sur la prop
// `type`, qu'on décide à l'exécution. Statiquement il ne voit que le défaut
// (`text`, donc string[]) et refuse la branche numérique.
const pinValue = computed(
  () => (props.charset === 'alphanumeric' ? digits.value : digits.value.map(Number)) as string[],
)

function sanitize(raw: string): string {
  return props.charset === 'alphanumeric'
    ? raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, props.length)
    : raw.replace(/\D/g, '').slice(0, props.length)
}

function publish(clean: string) {
  emit('update:modelValue', clean)
  if (clean.length === props.length) emit('complete', clean)
}

function onUpdate(value: (number | string)[]) {
  // Un trou du tableau (case vidée au milieu) se rend en chaîne vide, donc
  // join() suffit : même comportement que l'implémentation précédente.
  publish(sanitize(value.join('')))
}

// ─── Saisie en bloc ────────────────────────────────────────────────────────
//
// Un code arrive rarement caractère par caractère : on le colle, ou le
// téléphone le propose au-dessus du clavier (autocomplete one-time-code).
// reka-ui ne répartit le texte sur les cases que sur un évènement `paste`.
// Or le remplissage automatique d'iOS et d'Android, lui, passe par une
// insertion de texte ordinaire : les six caractères tombent alors dans UNE
// case, dont reka ne garde que le dernier. L'écran affichait un seul chiffre
// et le bouton « Vérifier le code » restait éteint sans rien expliquer.
//
// On intercepte donc les deux voies nous-mêmes, en capture et avant reka, pour
// ne dépendre d'aucune version : dès qu'une saisie apporte plus d'un
// caractère, elle remplit la grille entière.
function distribute(text: string): boolean {
  const clean = sanitize(text)
  if (clean.length < 2) return false
  publish(clean)
  // Le curseur finit là où l'oeil finit : sur la dernière case remplie.
  boxes()[Math.min(clean.length, props.length) - 1]?.focus()
  return true
}

// ⚠️ Les trois écoutes ci-dessous sont posées sur la RACINE, en capture : elles
// voient donc passer tout ce qui se produit à l'intérieur, y compris ce qui
// n'est pas une case. reka rend en plus des six cases un champ caché
// (intégration aux formulaires) qui REJOUE la valeur complète du code dans un
// évènement `input` bubblé, à chaque frappe. Le filet le prenait pour un
// remplissage automatique : il republiait le code entier et surtout ramenait le
// focus sur la DERNIÈRE case remplie, c'est à dire une case en arrière. Dès le
// deuxième chiffre, la frappe suivante retombait dans une case déjà pleine et
// le code se faisait écraser à chaque touche.
// Le marqueur `data-otp-box` distingue nos cases sans rien supposer du dessin
// interne de reka, qui a déjà changé une fois sous nos pieds.
function boxOf(event: Event): HTMLInputElement | null {
  const el = event.target as HTMLElement | null
  return el?.hasAttribute?.('data-otp-box') ? (el as HTMLInputElement) : null
}

function boxes(): HTMLInputElement[] {
  return Array.from(rootRef.value?.$el?.querySelectorAll<HTMLInputElement>('[data-otp-box]') ?? [])
}

function onPasteCapture(event: ClipboardEvent) {
  if (!boxOf(event)) return
  const text = event.clipboardData?.getData('text') ?? ''
  if (distribute(text)) {
    event.preventDefault()
    event.stopPropagation()
  }
}

function onBeforeInputCapture(event: InputEvent) {
  if (!boxOf(event)) return
  if (distribute(event.data ?? '')) {
    event.preventDefault()
    event.stopPropagation()
  }
}

// Filet : un moteur qui n'émettrait pas `beforeinput` laisse quand même la
// valeur complète dans la case au moment de `input`.
function onInputCapture(event: Event) {
  const box = boxOf(event)
  if (!box) return
  // Ce que la frappe apporte VRAIMENT. Une case déjà remplie qu'on revient
  // corriger contient « l'ancien caractère + le nouveau » : deux caractères,
  // mais aucun collage. Sans ce retrait, corriger un chiffre effaçait le code.
  const known = digits.value[boxes().indexOf(box)] ?? ''
  const inserted = known && box.value.startsWith(known) ? box.value.slice(known.length) : box.value
  if (inserted.length > 1 && distribute(inserted)) {
    event.stopPropagation()
  }
}

// Permet au parent (OtpEmailStep) de mettre le focus sur la 1re case à
// l'apparition de l'étape code (TW #17612000). ⚠️ iOS WKWebView : focus()
// programmatique peut ne pas ouvrir le clavier sans geste utilisateur ; le
// focus visuel reste utile et le scroll garantit la visibilité.
const rootRef = ref<{ $el?: HTMLElement } | null>(null)

function focusFirst() {
  boxes()[0]?.focus()
}

// La secousse est IMPÉRATIVE et pas dérivée de `invalid`. Deux codes faux de
// suite laissent `invalid` à `true` sans transition : une classe pilotée par
// la prop ne rejouerait rien, et le deuxième refus passerait inaperçu.
const shaking = ref(false)
let shakeTimer: ReturnType<typeof setTimeout> | null = null

function shake() {
  if (shakeTimer) clearTimeout(shakeTimer)
  shaking.value = false
  // Un tick sans la classe, sinon le navigateur ne voit aucun changement et ne
  // relance pas l'animation.
  requestAnimationFrame(() => {
    shaking.value = true
    shakeTimer = setTimeout(() => { shaking.value = false }, 400)
  })
}

onBeforeUnmount(() => { if (shakeTimer) clearTimeout(shakeTimer) })

defineExpose({ focusFirst, shake })
</script>

<style scoped>
/* Mêmes valeurs que les deux autres saisies de code de l'app
   (ParentJoinCodePage, ChildPinLoginPage) : 0.4s, ±3px. Un refus doit se
   sentir pareil quel que soit l'écran qui le prononce. */
@keyframes otp-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}

.otp-shake {
  animation: otp-shake 0.4s;
}

@media (prefers-reduced-motion: reduce) {
  .otp-shake {
    animation: none;
  }
}
</style>
